<template>
  <div class="detail">
    <div class="name">
      <h3>上报列表</h3>
    </div>
    <div class="operate">
      <el-select
        v-model="typeFilter"
        placeholder="全部类型"
        clearable
        style="width: 180px"
        @change="search"
      >
        <el-option
          v-for="item in typeOptions"
          :key="item"
          :label="item"
          :value="item"
        />
      </el-select>
      <el-input
        v-model="keyword"
        style="width: 240px"
        placeholder="按 message/url 搜索"
        clearable
        @keyup.enter="search"
      />
      <el-button type="primary" @click="search">查询</el-button>
      <el-button @click="load">刷新</el-button>
    </div>

    <el-table v-loading="loading" :data="tableData" style="width: 100%">
      <el-table-column label="时间" width="170">
        <template #default="{ row }">{{ fmtTime(row.receivedAt) }}</template>
      </el-table-column>
      <el-table-column label="类型" width="160">
        <template #default="{ row }">
          <el-tag size="small" effect="plain">{{
            row.data?.type || "unknown"
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag
            size="small"
            :type="row.data?.status === 'error' ? 'danger' : 'success'"
          >
            {{ row.data?.status || "-" }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="摘要" min-width="300" show-overflow-tooltip>
        <template #default="{ row }">{{ summarize(row.data) }}</template>
      </el-table-column>
      <el-table-column label="页面" width="180" show-overflow-tooltip>
        <template #default="{ row }">{{
          pagePath(row.data?.pageUrl)
        }}</template>
      </el-table-column>
      <el-table-column label="录屏" width="90" align="center">
        <template #default="{ row }">
          <el-button
            v-if="hasRecord(row)"
            link
            type="primary"
            @click="playRecord(row)"
          >
            播放
          </el-button>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="90" align="center">
        <template #default="{ row }">
          <el-button link type="primary" @click="showDetail(row)"
            >详情</el-button
          >
        </template>
      </el-table-column>
      <template #empty>暂无数据</template>
    </el-table>

    <div class="page">
      <el-pagination
        v-model:current-page="page"
        background
        layout="prev, pager, next, total"
        :page-size="pageSize"
        :total="total"
        @current-change="load"
      />
    </div>

    <el-dialog v-model="detailVisible" title="上报详情" width="900px">
      <el-tabs v-if="resolvedStack?.frames?.length" type="border-card">
        <el-tab-pane label="源码定位">
          <div v-loading="stackLoading" class="stack-section">
            <div
              v-for="(frame, idx) in resolvedStack.frames"
              :key="idx"
              class="stack-frame"
            >
              <div class="frame-header">
                <el-tag size="small" type="info">{{
                  frame.functionName || "anonymous"
                }}</el-tag>
                <span class="frame-location">
                  {{ frame.source || frame.filename }}:{{
                    frame.sourceLine || frame.line
                  }}:{{ frame.sourceColumn || frame.column }}
                </span>
              </div>
              <SourceCodeViewer
                v-if="frame.codeLines?.length"
                :lines="frame.codeLines"
                class="frame-code"
              />
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="原始数据">
          <pre class="detail-json">{{ detailContent }}</pre>
        </el-tab-pane>
      </el-tabs>
      <template v-else>
        <pre class="detail-json">{{ detailContent }}</pre>
      </template>
    </el-dialog>

    <RecordPlayer v-model="playerVisible" :events="playerEvents" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import {
  fetchReports,
  resolveStack,
  type ReportRecord,
  type ResolvedStack,
} from "../api";
import RecordPlayer from "./RecordPlayer.vue";
import SourceCodeViewer from "./SourceCodeViewer.vue";
import { ElMessage } from "element-plus";

const typeOptions = [
  "error",
  "unhandledRejection",
  "xhr",
  "fetch",
  "resource",
  "performance",
  "recordScreen",
  "whiteScreen",
  "custom",
];

const typeFilter = ref("");
const keyword = ref("");
const page = ref(1);
const pageSize = 10;
const total = ref(0);
const tableData = ref<ReportRecord[]>([]);
const loading = ref(false);

const detailVisible = ref(false);
const detailContent = ref("");
const resolvedStack = ref<ResolvedStack | null>(null);
const stackLoading = ref(false);
const playerVisible = ref(false);
const playerEvents = ref("");

async function load() {
  loading.value = true;
  try {
    const result = await fetchReports({
      type: typeFilter.value || undefined,
      keyword: keyword.value.trim() || undefined,
      page: page.value,
      pageSize,
    });
    total.value = result.total;
    tableData.value = result.list;
  } catch {
    ElMessage.error("获取上报列表失败，请确认 web-sdk/server 已启动");
  } finally {
    loading.value = false;
  }
}

function search() {
  page.value = 1;
  load();
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
}

function summarize(data: any): string {
  if (!data) return "-";
  if (data.isAggregate) return `[聚合 x${data.count}] ${data.message || ""}`;
  return data.message || data.name || data.url || "-";
}

function pagePath(url?: string): string {
  if (!url) return "-";
  try {
    return new URL(url).pathname + new URL(url).search;
  } catch {
    return url;
  }
}

function hasRecord(row: ReportRecord): boolean {
  return row.data?.type === "recordScreen" && !!row.data?.events;
}

function showDetail(row: ReportRecord) {
  detailContent.value = JSON.stringify(row, null, 2);
  resolvedStack.value = null;
  detailVisible.value = true;

  const stack = row.data?.stack;
  if (typeof stack === "string" && stack.trim()) {
    stackLoading.value = true;
    resolveStack(stack, row.data?.release)
      .then((result) => {
        resolvedStack.value = result;
      })
      .catch(() => {
        // 静默失败，不影响查看原始详情
      })
      .finally(() => {
        stackLoading.value = false;
      });
  }
}

function playRecord(row: ReportRecord) {
  playerEvents.value = row.data.events;
  playerVisible.value = true;
}

defineExpose({ load });
onMounted(load);
</script>

<style scoped>
.detail {
  width: 100%;
}

.operate {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.page {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.detail-json {
  max-height: 60vh;
  overflow: auto;
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
}

.stack-section {
  max-height: 60vh;
  overflow-y: auto;
}

.stack-frame {
  margin-bottom: 16px;
}

.stack-frame:last-child {
  margin-bottom: 0;
}

.frame-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.frame-location {
  font-family: monospace;
  font-size: 12px;
  color: #606266;
}

.frame-code {
  border-radius: 4px;
}
</style>
